import { useMemo } from 'react'
import { findTimelineEvent } from '@klear/order-event-ui/event-timeline-utils'
import { Link, useParams, useNavigate } from 'react-router'
import { useAgentContext } from '@klear/ui/contexts/agent-context'
import { Card } from '@klear/ui/components/card'
import { Skeleton } from '@klear/ui/components/skeleton'
import { ROUTE_ORDER_EVENTS, parseOrderIdFromDisplay } from '@klear/constants'
import { ArrowLeft, ArrowUpFromLine } from 'lucide-react'
import { Button } from '@klear/ui/components/button'
import { useOrderEventRefByIdDisplay, useOrderEventTimelineTree } from '@klear/ui/hooks/use-order-events'
import { timelineItemParentId } from '@klear/order-event/order-event-timeline-tree'
import AdminOrderEventItems from '@/app/order/AdminOrderEventItems'
import OrderContextMeta from '@/app/order/OrderContextMeta'
import OrderEventReviews from '@/app/order/OrderEventReviews'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

interface OrderEventDetailProps {
  isEditing?: boolean
  appPrefix: string
}

export default function OrderEventDetail({ isEditing = false, appPrefix }: OrderEventDetailProps) {
  const { eventId = '' } = useParams()
  useAgentContext([`Record ${eventId}.`])
  const navigate = useNavigate()
  const { ref: eventRef, isLoading: isRefLoading } = useOrderEventRefByIdDisplay(eventId)
  const { events, isLoading: isTreeLoading } = useOrderEventTimelineTree(eventRef?.orderId)
  const timelineEvent = eventRef ? findTimelineEvent(events, eventRef.id) : undefined
  useDocumentTitle(timelineEvent?.envelope.idDisplay ?? 'Order Event', timelineEvent?.envelope.traderName)
  const parentId = timelineEvent != null ? timelineItemParentId(timelineEvent) : undefined
  const parentIdDisplay = useMemo(() => {
    if (parentId == null) return undefined
    return findTimelineEvent(events, parentId)?.envelope.idDisplay
  }, [events, parentId])
  const envelopeOrderIdDisplay = timelineEvent?.envelope.orderIdDisplay
  const orderIdDisplay =
    envelopeOrderIdDisplay != null && parseOrderIdFromDisplay(envelopeOrderIdDisplay) != null
      ? envelopeOrderIdDisplay
      : undefined

  const isLoading = isRefLoading || isTreeLoading

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Card className="p-6">
          <Skeleton className="h-[200px]" />
        </Card>
      </div>
    )
  }

  if (!eventRef || !timelineEvent) {
    return (
      <div className="container mx-auto px-4 py-6">
        <Card className="p-6">
          <p className="text-muted-foreground">Event not found</p>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold">Order Event Details</h1>
          <OrderContextMeta
            idDisplay={timelineEvent.envelope.idDisplay}
            orderIdDisplay={orderIdDisplay}
            customerName={timelineEvent.envelope.customerName}
            traderId={timelineEvent.envelope.traderId ?? eventRef.traderId}
            traderName={timelineEvent.envelope.traderName}
          />
          <OrderEventReviews orderEventId={eventRef.id} />
        </div>
        <div className="flex items-center gap-2">
          {parentIdDisplay ? (
            <Button variant="outline" asChild>
              <Link to={`/${ROUTE_ORDER_EVENTS}/${parentIdDisplay}`}>
                <ArrowUpFromLine className="mr-2 h-4 w-4" />
                View Parent
              </Link>
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
      </div>

      <AdminOrderEventItems
        events={[timelineEvent]}
        traderId={eventRef.traderId}
        orderId={eventRef.orderId}
        timelineEvents={events}
        appPrefix={appPrefix}
        isEditing={isEditing}
        currentEventId={eventRef.id}
      />
    </div>
  )
}
